// variant-sync-guard.js
// 目的：見た目の選択と送信用 variant ID のズレを、送信直前＆変更時に静かに是正する。
// - Dawn標準のプルダウン / Globoスウォッチ のどちらでも動作
// - product-info.js は未改変のままでOK
// - AIGボタンも送信直前に同期（あれば）
// - できるだけ “元の仕組み” を壊さず、前段で正しいIDをセットするだけ

(function () {
  if (window.__variantSyncGuard) return;
  window.__variantSyncGuard = true;

  // -------- ユーティリティ --------
  const raf = (fn) => new Promise(r => requestAnimationFrame(() => { try { fn(); } finally { r(); } }));

  function getProductRoot(el) {
    const cands = ['product-form', 'form[action*="/cart/add"]', '.product', 'main'];
    for (const sel of cands) {
      const found = (el?.closest?.(sel)) || document.querySelector(sel);
      if (found) return found;
    }
    return document;
  }

  function getVariantIdInput(root) {
    return root.querySelector('input[name="id"]');
  }

  function readVariantsJSON() {
    try {
      const tag = document.getElementById('product-variants-json');
      if (tag?.textContent?.trim()) return JSON.parse(tag.textContent);
    } catch {}
    // Dawnのmetaフォールバック
    try {
      const v = window?.meta?.product?.variants;
      if (Array.isArray(v) && v.length) return v;
    } catch {}
    return null;
  }

  function normalizeLabel(t) {
    return String(t || '')
      .replace(/\s+/g, '')
      .replace(/[：:]/g, '')
      .toLowerCase();
  }

  function readSelectedOptionsFromGlobo(root) {
    const res = [];
    document.querySelectorAll('.globo-swatch-product-detail .swatch--gl').forEach((wrap, idx) => {
      const legend = wrap.querySelector('.name-option');
      const label = legend?.dataset?.name || legend?.textContent || `option${idx+1}`;
      const selected = wrap.querySelector('.select-option input[type="radio"]:checked') 
                    || wrap.querySelector('.select-option.selected input[type="radio"]');
      const val = selected?.value || selected?.closest('.select-option')?.dataset?.value;
      if (val) res.push({ label: normalizeLabel(label), value: String(val) });
    });
    return res.length ? res : null;
  }

  function readSelectedOptionsFromDawn(root) {
    const res = [];
    root.querySelectorAll('select[name^="options["]').forEach(sel => {
      const name = sel.getAttribute('name'); // options[カラー1] など
      const m = name && name.match(/^options\[(.+)\]$/);
      const label = m ? m[1] : name;
      const val = sel.value;
      if (val) res.push({ label: normalizeLabel(label), value: String(val) });
    });
    return res.length ? res : null;
  }

  function readSelectedOptions(root) {
    // まずはGlobo、無ければDawn
    return readSelectedOptionsFromGlobo(root) || readSelectedOptionsFromDawn(root) || [];
  }

  function findVariantByOptions(variants, selectedOptions) {
    if (!Array.isArray(variants) || !variants.length || !selectedOptions.length) return null;

    // Dawnのvariant.options は [opt1, opt2, opt3]
    // selectedOptions は [{label:'カラー1', value:'ミスト'}, ...] の配列
    const values = selectedOptions.map(o => o.value);

    // 完全一致を探す
    const hit = variants.find(v => {
      if (!Array.isArray(v.options)) return false;
      if (v.options.length < values.length) return false;
      for (let i = 0; i < values.length; i++) {
        if (String(v.options[i]) !== String(values[i])) return false;
      }
      return true;
    });
    return hit || null;
  }

  function setUrlVariantParam(variantId) {
    try {
      if (!variantId) return;
      const url = new URL(location.href);
      if (url.searchParams.get('variant') === String(variantId)) return;
      url.searchParams.set('variant', String(variantId));
      history.replaceState({}, '', url.toString());
    } catch {}
  }

  function setAigDefaultVariantId(root, variantId) {
    const btn = root.querySelector('#all-in-gift-checkout-button');
    const container = btn?.closest?.('.sns-gift-product-container');
    if (container && variantId) {
      container.dataset.defaultVariantId = String(variantId);
      container.setAttribute('data-default-variant-id', String(variantId));
    }
  }

  // -------- 同期の中核：現在の選択から正しいIDを導出し、hiddenとURLを直す --------
  async function reconcileVariant(root, variantsCache) {
    await raf(() => {});
    const idInput = getVariantIdInput(root);
    if (!idInput) return null;

    const allVariants = variantsCache.current || (variantsCache.current = readVariantsJSON());
    const selected = readSelectedOptions(root);
    if (!allVariants || !selected.length) return null;

    const found = findVariantByOptions(allVariants, selected);
    if (found && found.id) {
      const curr = idInput.value;
      if (String(curr) !== String(found.id)) {
        idInput.value = String(found.id);
      }
      // URL と AIG もあわせて更新（静かに）
      setUrlVariantParam(found.id);
      setAigDefaultVariantId(root, found.id);
      return found.id;
    }
    return null;
  }

  // -------- “送信直前” の静かなガード --------
  function bindSubmitGuard(root, variantsCache) {
    const form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // 二重送信ロック（短時間）
    let lock = false;
    form.addEventListener('submit', function onSubmit(e) {
      if (lock) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // 送信直前の同期
      const idInput = getVariantIdInput(root);
      const before = idInput?.value;

      // prevent → 同期 → 再送 の順にして元の仕組みを壊さず是正
      e.preventDefault();
      e.stopPropagation();

      reconcileVariant(root, variantsCache).then((newId) => {
        // hiddenが直っていればそのまま再送
        lock = true;
        setTimeout(() => (lock = false), 600);
        // 念のため再取得
        const nowId = getVariantIdInput(root)?.value || newId || before;
        // フォームに正しいIDが入っていればそのまま submit()
        // Dawnのproduct-form.jsがsubmitを拾ってAjax送信します（当ガードは前段で修正するだけ）
        try {
          // 再送前に hidden の value を確実に反映
          if (idInput && nowId) idInput.value = String(nowId);
          form.requestSubmit ? form.requestSubmit() : form.submit();
        } catch {
          // フォールバック
          form.submit();
        }
      });
    }, true);
  }

  // -------- AIGボタンクリックの静かなガード --------
  function bindAigGuard(root, variantsCache) {
    const aigBtn = root.querySelector('#all-in-gift-checkout-button');
    if (!aigBtn) return;

    let lock = false;
    aigBtn.addEventListener('click', (e) => {
      if (lock) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // 送信直前に同期
      e.preventDefault();
      e.stopPropagation();
      reconcileVariant(root, variantsCache).then(() => {
        lock = true;
        setTimeout(() => (lock = false), 600);
        // AIGは data-default-variant-id を見に行く実装が多いので、reconcile 内で上書き済み
        // ユーザー操作を尊重して再クリック（小さな遅延でUI更新を待つ）
        setTimeout(() => { try { aigBtn.click(); } catch {} }, 30);
      });
    }, true);
  }

  // -------- 変更イベントでの“静かな”追随（URL/hidden/AIGを整える） --------
  function bindChangeFollow(root, variantsCache) {
    const handler = () => reconcileVariant(root, variantsCache);
    // Globoのラジオ＆Dawnのselect
    root.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches?.('.globo-swatch-product-detail input[type="radio"], select[name^="options["]')) {
        handler();
      }
    }, true);

    // 一部テーマ/アプリはクリックだけで値が変わることがあるので click も拾う
    root.addEventListener('click', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.closest?.('.globo-swatch-product-detail .select-option')) {
        handler();
      }
    }, true);
  }

  // -------- 初期化 --------
  function init(rootNode) {
    const root = getProductRoot(rootNode || document.body);
    const variantsCache = { current: null };

    // 初期ロード時に1回整える（?variant の調整や hidden の初期ズレを吸収）
    reconcileVariant(root, variantsCache);

    bindChangeFollow(root, variantsCache);
    bindSubmitGuard(root, variantsCache);
    bindAigGuard(root, variantsCache);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // セクション再描画やクイックビュー対応
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  new MutationObserver((muts) => {
    for (const m of muts) {
      if ([...m.addedNodes].some(n =>
        n.nodeType === 1 && (
          n.matches?.('form[action*="/cart/add"], .globo-swatch-product-detail, #all-in-gift-checkout-button') ||
          n.querySelector?.('form[action*="/cart/add"], .globo-swatch-product-detail, #all-in-gift-checkout-button')
        )
      )) {
        init();
        break;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

})();
